import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Heart, Loader2, Search, SlidersHorizontal, ArrowLeft, MapPin, AlertCircle } from 'lucide-react';
import { usePets } from '@/hooks/usePets';
import { useAuth } from '@/contexts/AuthContext';
import { useMatchDiscovery } from '@/hooks/useMatchDiscovery';
import { MatchDiscoveryCard } from '@/components/MatchDiscoveryCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfile } from '@/hooks/useProfile';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

const FindMatches = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pets, isLoading: loadingPets } = usePets();
  const { data: profile } = useProfile();
  
  const [selectedPetId, setSelectedPetId] = useState('');
  const [matchType, setMatchType] = useState<'playdate' | 'breeding' | 'friendship' | 'adoption'>('playdate');
  const [maxDistance, setMaxDistance] = useState(50);
  const [minScore, setMinScore] = useState(0);
  const [showFilters, setShowFilters] = useState(true); // Show filters by default

  const userPets = pets?.filter(p => p.user_id === user?.id && !p.is_memorial) || [];
  const selectedPet = userPets.find(p => p.id === selectedPetId);
  const isBreedingWithNeuteredPet = matchType === 'breeding' && selectedPet?.is_neutered === true;
  const hasCoordinates = !!(profile?.latitude && profile?.longitude);

  const { data, isLoading: loadingMatches, refetch } = useMatchDiscovery({
    pet_id: selectedPetId,
    match_type: matchType,
    max_distance_km: maxDistance,
    min_score: minScore,
    limit: 20
  });

  useEffect(() => {
    if (userPets.length > 0 && !selectedPetId) {
      setSelectedPetId(userPets[0].id);
    }
  }, [userPets, selectedPetId]);

  const handleSearch = () => {
    refetch();
  };

  const MATCH_TYPE_OPTIONS = [
    { value: 'playdate', label: t('findMatches.matchTypes.playdate') },
    { value: 'friendship', label: t('findMatches.matchTypes.friendship') },
    { value: 'breeding', label: t('findMatches.matchTypes.breeding') },
    { value: 'adoption', label: t('findMatches.matchTypes.adoption') },
  ];

  return (
    <div className="container mx-auto max-w-5xl p-4 pb-24">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('findMatches.back')}
        </Button>
        <h1 className="text-3xl font-bold flex items-center gap-2 mb-2">
          <Heart className="h-8 w-8 text-pink-500" />
          {t('findMatches.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('findMatches.subtitle')}
        </p>
      </div>

      {/* Location Warning */}
      {!hasCoordinates && (
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong className="font-semibold">Location coordinates not set!</strong>
            <p className="mt-1 text-sm">
              To get accurate distance-based matches, please add your location with coordinates in your{' '}
              <Button
                variant="link"
                className="h-auto p-0 text-amber-800 underline font-semibold"
                onClick={() => navigate('/profile')}
              >
                profile settings
              </Button>
              . Without coordinates, matches will be limited and distances will be approximate.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters Card */}
      <Card className="p-6 mb-6">
        <div className="space-y-4">
          {/* Pet Selection */}
          <div className="space-y-2">
            <Label htmlFor="pet-select" className="text-base font-semibold">
              {t('findMatches.selectPet')}
            </Label>
            {loadingPets ? (
              <div className="h-10 bg-muted animate-pulse rounded-md" />
            ) : userPets.length === 0 ? (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertDescription className="text-amber-800">
                  {t('findMatches.noPetsAdded')}{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-amber-800 underline"
                    onClick={() => navigate('/my-pets')}
                  >
                    {t('findMatches.addPet')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <Select value={selectedPetId} onValueChange={setSelectedPetId}>
                <SelectTrigger id="pet-select">
                  <SelectValue placeholder={t('findMatches.choosePet')} />
                </SelectTrigger>
                <SelectContent>
                  {userPets.map(pet => (
                    <SelectItem key={pet.id} value={pet.id}>
                      {pet.name} ({translateSpeciesSingular(pet.species, t)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Match Type */}
          <div className="space-y-2">
            <Label htmlFor="match-type">{t('findMatches.matchType')}</Label>
            <Select value={matchType} onValueChange={(v: any) => setMatchType(v)}>
              <SelectTrigger id="match-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATCH_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Warning for neutered pets trying to breed */}
            {isBreedingWithNeuteredPet && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertDescription className="text-amber-800">
                  {t('findMatches.neuteredWarning')}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            {showFilters ? t('findMatches.hideFilters') : t('findMatches.showFilters')} {t('findMatches.advancedFilters')}
          </Button>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="space-y-4 pt-4 border-t">
              {/* Max Distance */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">
                    {t('findMatches.maxDistance')}
                    {hasCoordinates && <MapPin className="inline h-3.5 w-3.5 ml-1 text-green-600" />}
                  </Label>
                  <Badge variant="secondary" className="text-base font-bold">
                    {maxDistance} {t('findMatches.km')}
                  </Badge>
                </div>
                <Slider
                  value={[maxDistance]}
                  onValueChange={(v) => setMaxDistance(v[0])}
                  min={5}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {!hasCoordinates ? (
                    <span className="text-amber-600 font-medium">
                      ⚠️ Add your coordinates for accurate distance filtering
                    </span>
                  ) : maxDistance <= 10 
                    ? t('findMatches.distanceNearby')
                    : maxDistance <= 30
                    ? t('findMatches.distanceLocal')
                    : maxDistance <= 60
                    ? t('findMatches.distanceRegional')
                    : t('findMatches.distanceLong')}
                </p>
              </div>

              {/* Min Compatibility Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">{t('findMatches.minCompatibility')}</Label>
                  <Badge variant="secondary" className="text-base font-bold">
                    {minScore}%
                  </Badge>
                </div>
                <Slider
                  value={[minScore]}
                  onValueChange={(v) => setMinScore(v[0])}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {minScore === 0 
                    ? t('findMatches.compatibilityAll')
                    : t('findMatches.compatibilityMin', { minScore })}
                </p>
              </div>
            </div>
          )}

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            disabled={!selectedPetId || loadingMatches || isBreedingWithNeuteredPet}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
          >
            {loadingMatches ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('findMatches.searching')}
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                {t('findMatches.findMatchesBtn')}
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Results */}
      {loadingMatches && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loadingMatches && data && (
        <>
          {/* Results Header */}
          {data.matches.length > 0 && (
            <div className="mb-4">
              <h2 className="text-lg font-semibold">
                {t('findMatches.foundMatches', { count: data.count })}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('findMatches.sortedByScore')}
              </p>
            </div>
          )}

          {/* Match Cards */}
          {data.matches.length > 0 ? (
            <div className="space-y-4">
              {data.matches.map(match => (
                <MatchDiscoveryCard
                  key={match.pet_id}
                  match={match}
                  onRequestMatch={() => refetch()}
                />
              ))}
            </div>
          ) : selectedPetId ? (
            <Card className="p-12 text-center">
              <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('findMatches.noMatchesFound')}</h3>
              
              {/* Mensaje contextual basado en filtros */}
              <p className="text-muted-foreground mb-4">
                {minScore > 70 
                  ? t('findMatches.noMatchesCompatibility', { minScore })
                  : maxDistance < 30
                  ? t('findMatches.noMatchesDistance', { maxDistance })
                  : matchType === 'breeding'
                  ? t('findMatches.noMatchesBreeding')
                  : t('findMatches.noMatchesSpecies')}
              </p>
              
              {/* Botones de acción contextual */}
              <div className="flex gap-2 justify-center flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => {
                    setMinScore(0);
                    setMaxDistance(100);
                    handleSearch();
                  }}
                >
                  {t('findMatches.removeFilters')}
                </Button>
                
                {maxDistance < 50 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMaxDistance(100);
                      handleSearch();
                    }}
                  >
                    {t('findMatches.expandDistance')}
                  </Button>
                )}
                
                <Button
                  variant="default"
                  onClick={() => navigate('/my-pets')}
                >
                  {t('findMatches.addMorePets')}
                </Button>
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
};

export default FindMatches;
